import React, { useCallback, useMemo } from "react";
import { AddToChatButton } from "@components/tasks/AddToChatButton";
import { useAddCounterpartiesToChat } from "@components/tasks/useAddReferencesToChat";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";

interface Props {
    selectedRows: CounterpartyDirectoryViewRow[];
    onClearSelection: () => void;
}

/**
 * Attaches the selected counterparties as chat references, mirroring
 * TransactionsTaskAskButton for the transactions task body. Renders nothing
 * outside a task context, since it needs CounterpartyRefProvider/TasksProvider
 * to be mounted.
 */
export const DirectoryAskAboutSelectedButton: React.FC<Props> = ({
    selectedRows,
    onClearSelection,
}) => {
    const { canAddToChat, addCounterpartiesToChat } =
        useAddCounterpartiesToChat();

    const counterparties = useMemo(
        () =>
            selectedRows
                .map((row) => row.counterparty)
                .filter((cp): cp is NonNullable<typeof cp> => cp != null),
        [selectedRows],
    );

    const handleAttach = useCallback(() => {
        addCounterpartiesToChat(counterparties);
        onClearSelection();
    }, [addCounterpartiesToChat, counterparties, onClearSelection]);

    if (!canAddToChat || counterparties.length === 0) {
        return null;
    }

    return (
        <AddToChatButton
            noun="counterparty"
            count={counterparties.length}
            singleItemName={counterparties[0].name}
            onClick={handleAttach}
        />
    );
};
